import { CheckBox } from '@iwoca/orion';
import { clsx } from 'clsx';
import { set } from 'lodash-es';
import { useFormContext } from 'react-hook-form';
import * as Yup from 'yup';

import { useDebounceEmailAvailability } from './useDebounceEmailAvailability';
import {
  useGetContext,
  useGetProfile,
  useGetStateByStateKey,
} from '../../../api/lending/lapiHooks';
import { HookFormInput } from '../../../components/Input/Input';
import { postLendingApiJson } from '../../../Pages/lendingApiFetch';
import { getPrimaryEmailAddressForCustomerState } from '../../../Seller/AccessUsers/AccessUsers';
import { extractError } from '../../../utils/ReactHookForm';
import { useSuspenseIsLockedOutDueToDecline } from '../../hooks/useSuspenseIsLockedOutDueToDecline';
import { CheckoutButton } from '../../PayLinkLanding/PaymentDetails/PaymentDetails';
import { FormValues } from '../../SpendingLimitRequest/components/EmailDetails/EmailDetails';
import { DeclineLockOutMessage } from '../DeclineLockOutMessage/DeclineLockOutMessage';
import { LoginLink } from '../LoginLink/LoginLink';

const EMAIL_FIELD_ID = 'emailAddress';
function EmailInput({
  loggedIn = false,
  isSpendingLimit = false,
}: {
  loggedIn: boolean;
  isSpendingLimit?: boolean;
}) {
  const { formState, trigger } = useFormContext();
  const { touchedFields, errors } = formState;
  const error = extractError(errors[EMAIL_FIELD_ID]);

  const { state, refetchState } = useGetStateByStateKey();
  const { getContext } = useGetContext();
  const { getProfile } = useGetProfile();
  const loggedInCustomerEmailAddress =
    getPrimaryEmailAddressForCustomerState(state);

  const hideLabel = loggedIn || isSpendingLimit;

  return (
    <div className="pt-l">
      <HookFormInput
        label="Create your iwocaPay account"
        labelDescriptionText={
          !loggedIn
            ? 'Enter your email address to create your account or log in.'
            : ''
        }
        type="text"
        name={EMAIL_FIELD_ID}
        placeholder={
          loggedInCustomerEmailAddress || 'Business email address...'
        }
        onChange={async () => {
          set(formState.touchedFields, EMAIL_FIELD_ID, true);
          await trigger(EMAIL_FIELD_ID);
        }}
        disabled={Boolean(loggedInCustomerEmailAddress)}
        showError={false}
        data-testid="emailSignupInput"
        className={clsx(
          '[&_label]:mb-s [&_label]:text-xl [&_label]:font-bold',
          {
            '[&_label]:sr-only': hideLabel,
          },
        )}
      />

      {Boolean(loggedInCustomerEmailAddress) && (
        <p className="relative mx-0 my-[0.8rem] text-m font-reg">
          Not you?{' '}
          <button
            type="button"
            className="cursor-pointer border-none bg-transparent p-0 text-m text-denim-45 no-underline"
            onClick={async () => {
              await postLendingApiJson({ url: '/api/lending/edge/logout/' });
              await Promise.all([getContext(), getProfile(), refetchState()]);
            }}
          >
            Log out
          </button>
        </p>
      )}

      {touchedFields[EMAIL_FIELD_ID] && errors[EMAIL_FIELD_ID] && (
        <>
          {error === 'EMAIL_REGISTERED_MESSAGE' ? (
            <LoginLink />
          ) : (
            <div className="text-m font-med text-coral-35">{error}</div>
          )}
        </>
      )}
    </div>
  );
}

const getButtonText = ({
  loggedIn = false,
  isSpendingLimit = false,
}: {
  loggedIn: boolean;
  isSpendingLimit?: boolean;
}) => {
  if (isSpendingLimit && !loggedIn) {
    return 'Create account and continue';
  }
  if (isSpendingLimit && loggedIn) {
    return 'Continue';
  }
  if (!isSpendingLimit && !loggedIn) {
    return 'Create account and check out';
  }
  return 'Finish checkout';
};

const SpendingLimitCheckoutButton = ({
  isDisabled,
  isLoading,
  buttonText,
}: {
  isDisabled: boolean;
  isLoading: boolean;
  buttonText: string;
}) => {
  const declineLockOut = useSuspenseIsLockedOutDueToDecline();

  return (
    <>
      <CheckoutButton
        isDisabled={isDisabled || declineLockOut.isLockedOut}
        isLoading={isLoading}
        data-testid="continueStep0Button"
        buttonText={buttonText}
      />
      <DeclineLockOutMessage />
    </>
  );
};

export const EmailFormInput = ({
  loggedIn = false,
  isSpendingLimit = false,
}: {
  loggedIn: boolean;
  isSpendingLimit?: boolean;
}) => {
  const { formState, register } = useFormContext();

  const errors = formState.errors;
  const isSubmitting = formState.isSubmitting;

  const EMAIL_FIELD_ID = 'emailAddress';
  const isFirstTimeTouched =
    !formState.touchedFields[EMAIL_FIELD_ID] && !loggedIn;

  const isDisabled =
    isSubmitting || Object.keys(errors).length !== 0 || isFirstTimeTouched;

  const buttonText = getButtonText({ loggedIn, isSpendingLimit });

  return (
    <>
      <div className="mb-l">
        <EmailInput loggedIn={loggedIn} isSpendingLimit={isSpendingLimit} />
      </div>
      {isSpendingLimit ? (
        <SpendingLimitCheckoutButton
          isDisabled={isDisabled}
          isLoading={isSubmitting}
          buttonText={buttonText}
        />
      ) : (
        <CheckoutButton
          isDisabled={isDisabled}
          isLoading={isSubmitting}
          data-testid="continueStep0Button"
          buttonText={buttonText}
        />
      )}
      <div className="mx-auto my-r flex justify-center">
        {!loggedIn && (
          <CheckBox
            {...register('marketingOptIn')}
            id="marketingOptIn"
            label="Sign me up for small business news"
          />
        )}
      </div>
    </>
  );
};
export const useEmailValidation = () => {
  const debounceEmailAvailability = useDebounceEmailAvailability();
  const { state: customerState } = useGetStateByStateKey();
  const loggedInCustomerEmailAddress =
    getPrimaryEmailAddressForCustomerState(customerState);

  const validate = async (values: FormValues) => {
    const errors: { [k: string]: string } = {};

    if (!loggedInCustomerEmailAddress) {
      const isEmailValid = Yup.string()
        .email()
        .required()
        .isValidSync(values.emailAddress);

      if (isEmailValid) {
        let isEmailAvailable: null | boolean = null;
        try {
          isEmailAvailable = (await debounceEmailAvailability(
            values.emailAddress,
          )) as boolean;

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e: unknown) {}

        if (isEmailAvailable === false) {
          errors.emailAddress = 'EMAIL_REGISTERED_MESSAGE';
        }
      } else {
        errors.emailAddress = 'Please enter a valid email address';
      }
    }

    if (Object.keys(errors).length > 0) {
      return {
        values: {},
        errors,
      };
    }

    return {
      values,
      errors: {},
    };
  };

  return validate;
};
